import { EditorSection } from '@/components/plot/editor/EditorSection';
import { useFigureCompositionStore } from '@/store/figure-composition';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CURATED_FONTS } from '@/theme/fonts.manifest';

export function FigureStyleTab() {
  const currentComposition = useFigureCompositionStore((state) => state.currentComposition);
  const setTheme = useFigureCompositionStore((state) => state.setTheme);

  if (!currentComposition) return null;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="space-y-1">
        <h3 className="text-xs font-semibold text-foreground">Style</h3>
        <p className="text-[11px] text-muted-foreground">
          Set fonts, color mode, and accessibility for the whole figure.
        </p>
      </div>

      <EditorSection
        id="typography"
        title="Typography"
        description="Set font family and size scaling."
      >
        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Font family</Label>
            <Select
              value={currentComposition.theme.fontFamily || CURATED_FONTS[0].value}
              onValueChange={(value) => {
                setTheme({ fontFamily: value });
              }}
            >
              <SelectTrigger className="mt-1">
                <SelectValue>
                  {(() => {
                    const currentFont = currentComposition.theme.fontFamily || CURATED_FONTS[0].value;
                    const fontLabel = CURATED_FONTS.find(f => f.value === currentFont)?.label || CURATED_FONTS[0].label;
                    return fontLabel;
                  })()}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {CURATED_FONTS.map(font => (
                  <SelectItem key={font.value} value={font.value}>
                    <span data-font-name={font.label}>{font.label}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Font size multiplier</Label>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                step="0.1"
                min="0.5"
                max="2.0"
                className="w-20"
                value={currentComposition.theme.fontScale ?? 1.0}
                onChange={(e) => {
                  const scale = parseFloat(e.target.value);
                  if (!isNaN(scale) && scale >= 0.5 && scale <= 2.0) {
                    setTheme({ fontScale: scale });
                  }
                }}
              />
              <span className="text-xs text-muted-foreground">×</span>
            </div>
            <p className="text-[10px] text-muted-foreground">
              Scales all text sizes (0.5×–2×)
            </p>
          </div>
        </div>
      </EditorSection>

      <EditorSection
        id="colors"
        title="Colors"
        description="Set export color space and accessibility options."
      >
        <div className="space-y-3">
          <div className="flex flex-col gap-1">
            <Label className="text-xs">Export color space</Label>
            <RadioGroup
              value={currentComposition.theme.colorMode}
              onValueChange={(value) => setTheme({ colorMode: value as 'RGB' | 'CMYK' })}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="RGB" id="color-rgb" />
                <Label htmlFor="color-rgb" className="text-sm cursor-pointer">
                  RGB
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="CMYK" id="color-cmyk" />
                <Label htmlFor="color-cmyk" className="text-sm cursor-pointer">
                  CMYK
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-xs">Use colorblind-safe palette</Label>
            <Switch
              checked={currentComposition.theme.colorblindSafe ?? false}
              onCheckedChange={(checked) => setTheme({ colorblindSafe: checked })}
            />
          </div>
        </div>
      </EditorSection>
    </div>
  );
}

